import React, { useMemo, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Sidebar } from '../Sidebar/Sidebar';
import { AIMiniChat } from '../AIAssistant/AIMiniChat';
import { AppMode, LeadPageType, ClientPageType } from '@/shared/types/appTypes';
import { useTheme } from '@/shared/contexts/ThemeContext';
import { useLocation } from 'react-router-dom';
import { useUser } from '@/shared/contexts/UserContext';
import { useAICommand } from '@/shared/contexts/AICommandContext';
import { useLeadsContext } from '@/shared/contexts/LeadsContext';
import { useLeadsPage } from '@/contexts/LeadsPageContext';
import { MOCK_PROJECTS } from '@/domain';

interface TailwindPlusLayoutProps {
  children: React.ReactNode;
  mode: AppMode;
  onModeChange: (mode: AppMode) => void;
  currentPage: LeadPageType | ClientPageType;
  leadCount?: number;
  clientCount?: number;
}

export const TailwindPlusLayout: React.FC<TailwindPlusLayoutProps> = ({
  children,
  mode,
  onModeChange,
  currentPage,
  leadCount = 0,
  clientCount = 0,
}) => {
  const { theme } = useTheme();
  const location = useLocation();
  const { currentUser } = useUser();
  const { emitCommand } = useAICommand();
  const { leads } = useLeadsContext();

  // Ref para el contenedor principal
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Obtener viewMode del contexto (puede ser null si no estamos en LeadsPage)
  const leadsPageContext = (() => {
    try {
      return useLeadsPage();
    } catch {
      return null;
    }
  })();

  // Obtener el aiProfileId del proyecto asignado al usuario
  const aiProfileId = useMemo(() => {
    const projectId = currentUser.assignedProjects[0];
    if (!projectId) return 'bossco'; // Default fallback

    const project = MOCK_PROJECTS.find(p => p.id === projectId);
    return project?.aiProfileId || 'bossco';
  }, [currentUser.assignedProjects]);

  // Animar transiciones de página
  useEffect(() => {
    const ctx = gsap.context(() => {
      if (mainContentRef.current) {
        // Animación de fade in desde arriba
        gsap.fromTo(
          mainContentRef.current,
          {
            opacity: 0,
            y: 10,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.4,
            ease: 'power2.out',
          }
        );
      }
    });

    return () => ctx.revert();
  }, [location.pathname]); // Se ejecuta cada vez que cambia la ruta

  return (
    <div className="flex h-full relative overflow-hidden text-gray-900 dark:text-gray-100">
      {/* Fondo con gradiente mesh orgánico conectado */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-orange-50/30 to-blue-100/50 dark:from-[#2D2D2D] dark:via-[#2F2F2F] dark:to-[#2D2D2D]">
        {/* Capa de gradientes radiales conectados - Light mode */}
        <div className="absolute inset-0 dark:hidden" style={{
          backgroundImage: `
            radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
            radial-gradient(at 100% 0%, rgba(251, 146, 60, 0.12) 0px, transparent 50%),
            radial-gradient(at 100% 100%, rgba(59, 130, 246, 0.1) 0px, transparent 50%),
            radial-gradient(at 0% 100%, rgba(251, 146, 60, 0.15) 0px, transparent 50%),
            radial-gradient(at 50% 50%, rgba(147, 197, 253, 0.08) 0px, transparent 50%)
          `
        }}></div>
        {/* Dark mode: gradientes sutiles cálidos sobre gris estilo Claude */}
        <div className="absolute inset-0 hidden dark:block" style={{
          backgroundImage: `
            radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.06) 0px, transparent 50%),
            radial-gradient(at 100% 0%, rgba(251, 146, 60, 0.04) 0px, transparent 50%),
            radial-gradient(at 100% 100%, rgba(59, 130, 246, 0.05) 0px, transparent 50%),
            radial-gradient(at 0% 100%, rgba(251, 146, 60, 0.06) 0px, transparent 50%),
            radial-gradient(at 50% 50%, rgba(100, 100, 100, 0.04) 0px, transparent 50%)
          `
        }}></div>
      </div>

      {/* Contenido (encima del fondo) */}
      <div className="relative z-10 flex w-full h-full overflow-hidden">
      {/* Sidebar - Navegación de dos niveles */}
      <div className="flex-shrink-0 h-full overflow-visible">
        <Sidebar
          currentPage={currentPage}
          leadCount={leadCount}
          clientCount={clientCount}
        />
      </div>

      {/* Main Content Area - Ocupa el espacio central */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <main ref={mainContentRef} className="flex-1 min-h-0 overflow-hidden">
          {children}
        </main>
      </div>

      {/* AI Mini Chat - Desactivado temporalmente */}
      {/* <AIMiniChat
        onCommand={emitCommand}
        currentPage={currentPage}
        viewMode={leadsPageContext?.viewMode}
        userId={currentUser.id}
        userName={currentUser.name}
        projectId={aiProfileId}
        context={{
          totalLeads: leads.length,
          visibleLeads: leads.length,
          selectedCount: 0,
          currentFilters: {},
          currentView: 'all',
          currentLayout: 'grid',
        }}
      /> */}
      </div>
    </div>
  );
};